import type { CountryConfig } from './types.js';
import { esPublic } from './es.public.js';
import { CIF_ENTITY_LETTERS, isValidCif, isValidNif } from './es-nif.js';

export {
  CIF_ENTITY_LETTERS,
  cifControlDigit,
  isValidCif,
  isValidDni,
  isValidNie,
  isValidNif,
} from './es-nif.js';

/**
 * Is this NIF a LEGAL ENTITY (a CIF)? Natural persons — DNI, NIE and the K/L/M
 * forms — are autónomos and private individuals: their company pages are never
 * indexed (GDPR; owner, AGENT-RULES §6). A foreign or malformed id is not a
 * Spanish legal entity either, so it is not indexed.
 */
export function isLegalEntityNif(id: string): boolean {
  return isValidCif(id);
}

/**
 * The SHAPE of a legal entity's NIF (a CIF): entity letter, seven digits, a
 * control digit or letter. Plain character classes only, so the same source
 * reads identically as a JavaScript regex and as a Postgres `~` pattern — the
 * company step suggests only ids of this shape (`companyId.suggestPattern`).
 */
export const ES_CIF_SHAPE = `^[${CIF_ENTITY_LETTERS}][0-9]{7}[0-9A-J]$`;

/** A CPV code: 8 digits, optionally followed by its `-d` check digit. */
const CPV_CODE = /^(\d{8})(?:-\d)?$/;

export const esConfig: CountryConfig = {
  ...esPublic,
  // The four official PLACSP open-data feeds — all `TenderSourceAdapter`s
  // (adapter: branch ml/placsp). `placsp-menores` writes `tender_results` only,
  // never the catalogue; `placsp-preliminares` (feed 1403, consultas preliminares del
  // mercado) writes catalogue rows only. Each also needs its `tender_sources` row
  // (`prisma/es-seed.ts`, seeded disabled).
  sourceKeys: ['placsp', 'placsp-agregadas', 'placsp-menores', 'placsp-preliminares'],
  // `tender_results.method` holds the official CODICE procedure name
  // («Abierto», «Negociado sin publicidad», …) and «Contrato menor» for feed
  // 1143. A negotiated procedure without publicity and a minor contract are
  // awarded without an open competition. Markers are lower-case substrings
  // (the schema requires it) matched case-insensitively. A row with no method is
  // not counted as a competition — the same call as Kazakhstan.
  competition: {
    nonCompetitiveSources: [],
    nonCompetitiveMethodMarkers: ['sin publicidad', 'contrato menor'],
    unknownMethodIsCompetitive: false,
  },
  companyId: {
    label: 'NIF',
    validate: isValidNif,
    // Suggestions offer legal entities only: a natural person's NIF is never
    // looked up by name or number for somebody else (GDPR — see the type).
    suggestPattern: ES_CIF_SHAPE,
    // A typed name matches without its accents and without the legal form:
    // «telefonica» finds «TELEFÓNICA DE ESPAÑA, S.A.U.», «limpiezas sl» finds
    // every «LIMPIEZAS …» (`company-suggest.ts`).
    nameSearch: 'es',
    // After a known NIF: «12 contratos (3 licitaciones y 9 menores), sobre todo
    // en …» — every award, not only the competitive ones (`company-step.service.ts`).
    payoff: 'es',
  },
  // CPV arrives in the feed itself (8 digits, `45233142`, sometimes with a `-7`
  // check digit). Like KZ: keep a source-provided code, otherwise decline — no
  // keyword classifier for Spanish titles yet. The code is CANONICALISED to the
  // bare 8 digits: the CPV reference, category and kind lookups all key on them,
  // and a stored `45233142-7` would miss every one. Anything that is not a CPV
  // code (blank included) is no code.
  classifier: {
    resolve: (code, _title) => (code ? (CPV_CODE.exec(code.trim())?.[1] ?? null) : null),
    classify: (_title) => null,
  },
  // ≥2 competitive awards in 36 months, no ACTIVE test (no Spanish register we
  // read says ACTIVE), and LEGAL ENTITIES ONLY — `indexableId` keeps every
  // natural person's page out of the index, grandfather arm included.
  companyIndex: {
    minCompetitiveAwards: 2,
    windowMonths: 36,
    requireActiveStatus: false,
    indexableId: isLegalEntityNif,
  },
  // Euros. Floor 1 €: a contrato menor can legitimately be a few euros, but 0 € and
  // cent-only amounts are placeholders. Ceiling 50 000 M€: roughly 10× the largest
  // Spanish awards (multi-billion defence and rail programmes, ~4–5 mil M€).
  awardPlausibility: { floor: 1, ceiling: 50_000_000_000 },
  // An existing account always gets the emailed code at `POST /auth/start` —
  // never a silent sign-in on its address alone (measured on the live site,
  // 2026-10-20: an unverified account was handed its session). Mail works here.
  auth: { codeRequiredForExisting: true },
  // Persona test 22.10 (D12a): the plain browse is where an ad visitor lands, and its first
  // twelve cards were «Consulta preliminar del mercado» — notices that are not tenders, seeded
  // with the newest timestamps. They stay everywhere else (filters, search, their own page).
  home: { excludeSources: ['placsp-preliminares'] },
  // Owner, 2026-10-23: Lucía consults fully and sets up or adjusts the writer's saved
  // search herself (`lucia-search.service.ts`) instead of handing the letter over.
  support: { assistantManagesSearches: true },
};
